import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import {
  useWhatsappConversations,
  whatsappConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import { useCallback, useEffect, useMemo } from "react";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { WhatsappConversationTile } from "../WhatsappConversationTile";
import { ConversationListEmptyState } from "../ConversationListEmptyState";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";

export interface WhatsappConversationsProps {
  selectedFilter: CRMConversationFilter;
  setSelectedFilter: (value: CRMConversationFilter) => void;
  selectedMode: CRMConversationMode;
  searchQuery: string;
}

export const WhatsappConversations: React.FC<WhatsappConversationsProps> = ({
  selectedFilter,
  setSelectedFilter,
  selectedMode,
  searchQuery,
}) => {
  const {
    selectedConversation,
    selectConversation,
    incomingWhatsappMessages$,
  } = useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [fetchConversationById] =
    integrationsApi.useLazyGetWhatsappConversationByIdQuery();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for WhatsApp based on selected mode
  const computedFilter = useMemo(() => {
    if (selectedFilter !== CRMConversationFilter.active) {
      return selectedFilter;
    }
    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return selectedFilter;
    }
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
  } = useWhatsappConversations({
    entityId,
    filter: computedFilter,
    searchTerm: searchQuery,
    pageSize: 10,
  });

  useSelectedConversation({
    channel: CRMChannel.whatsapp,
    selectedFilter,
    setSelectedFilter,
  });

  const prependConversationById = useCallback(
    async (convId: string) => {
      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        whatsappConversations$.next({
          type: "prepend",
          conversation: reactivateClosedConversation(response.data),
        });
      }
    },
    [entityId, fetchConversationById]
  );

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingWhatsappMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        prependConversationById(message.conv_id);
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as WhatsappConversationModel).payload
              .timestamp >= message.payload.timestamp) ||
          message.msg_status === "new"
        ) {
          const updatedConversation = reactivateClosedConversation({
            ...selectedConversation,
            ...message,
          });
          whatsappConversations$.next({
            type: "update",
            conversation: updatedConversation as WhatsappConversationModel,
          });
          selectConversation(
            updatedConversation as WhatsappConversationModel,
            true
          );
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.payload.timestamp <= message.payload.timestamp &&
        message.msg_status === "new"
      ) {
        const updatedConversation: WhatsappConversationModel =
          reactivateClosedConversation({
            ...existingConversation,
            ...message,
          });

        if (message.type === "incoming") {
          updatedConversation.since = message.payload.timestamp;
        }
        // Prepend conversation to bump it to the top
        whatsappConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        // Also update selected conversation if needed
        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingWhatsappMessages$,
    conversations,
    prependConversationById,
    selectedConversation,
    selectConversation,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <WhatsappConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
          isDraftUnavailable={selectedFilter !== CRMConversationFilter.active}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      <ConversationListEmptyState
        hasConversations={conversations.length > 0}
        searchQuery={searchQuery}
        selectedFilter={selectedFilter}
        selectedMode={selectedMode}
      />
    </DataLoadingWrapper>
  );
};
